// localStorage-backed eligibility helpers for the profile-photo nudge,
// mirroring the view-count/cooldown shape of PopupBannerModal
// (src/components/PopupBannerModal.tsx) so behavior feels consistent with
// the other nudges/popups already in the app.

interface NudgeState {
  viewCount: number;
  lastShownAt: string | null;
}

const COOLDOWN_MS = 48 * 60 * 60 * 1000; // 48 hours
const MAX_VIEW_COUNT = 3;

function stateKey(id: string): string {
  return `profile_photo_nudge_state_${id}`;
}

function readState(id: string): NudgeState {
  try {
    const raw = localStorage.getItem(stateKey(id));
    if (raw) {
      return JSON.parse(raw) as NudgeState;
    }
  } catch {
    // Ignore parse errors — start fresh.
  }
  return { viewCount: 0, lastShownAt: null };
}

function writeState(id: string, state: NudgeState): void {
  try {
    localStorage.setItem(stateKey(id), JSON.stringify(state));
  } catch {
    // Ignore storage errors (private browsing, quota exceeded, etc.).
  }
}

/** Returns true when a session-gated nudge (e.g. first login) is eligible to be shown again. */
export function isProfilePhotoNudgeEligible(id: string): boolean {
  const state = readState(id);
  if (state.viewCount >= MAX_VIEW_COUNT) {
    return false;
  }
  if (state.lastShownAt !== null) {
    const elapsed = Date.now() - new Date(state.lastShownAt).getTime();
    if (elapsed < COOLDOWN_MS) {
      return false;
    }
  }
  return true;
}

/** Records that a session-gated nudge was just shown. */
export function recordProfilePhotoNudgeShown(id: string): void {
  const current = readState(id);
  writeState(id, {
    viewCount: current.viewCount + 1,
    lastShownAt: new Date().toISOString(),
  });
}

const BANNER_DISMISS_KEY = "profile_photo_banner_dismissed_until";
const BANNER_DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/** Whether the persistent home/profile banner was dismissed recently. */
export function isProfilePhotoBannerDismissed(): boolean {
  try {
    const raw = localStorage.getItem(BANNER_DISMISS_KEY);
    if (!raw) return false;
    return Date.now() < Number(raw);
  } catch {
    return false;
  }
}

/** Dismisses the persistent banner for BANNER_DISMISS_MS — it resurfaces after that, since it isn't permanent until the user actually has a custom photo. */
export function dismissProfilePhotoBanner(): void {
  try {
    localStorage.setItem(BANNER_DISMISS_KEY, String(Date.now() + BANNER_DISMISS_MS));
  } catch {
    // Ignore storage errors.
  }
}

const FIRST_MESSAGE_FLAG_KEY = "profile_photo_nudge_first_message_shown";

/** Whether the first-chat-message nudge has already fired once for this browser. */
export function hasShownFirstMessageNudge(): boolean {
  try {
    return localStorage.getItem(FIRST_MESSAGE_FLAG_KEY) === "true";
  } catch {
    return false;
  }
}

export function markFirstMessageNudgeShown(): void {
  try {
    localStorage.setItem(FIRST_MESSAGE_FLAG_KEY, "true");
  } catch {
    // Ignore storage errors.
  }
}
